import React from 'react'
import { Grid, Container } from '../layout'
import { BusinessCard, BusinessCardProps } from './BusinessCard'

export interface BusinessGridProps {
  /**
   * Array of business data
   */
  businesses: BusinessCardProps[]
  /**
   * Loading state
   */
  loading?: boolean
  /**
   * Error state
   */
  error?: string | null
  /**
   * Empty state message
   */
  emptyMessage?: string
  /**
   * On business click
   */
  onBusinessClick?: (id: string) => void
  /**
   * On business favorite
   */
  onBusinessFavorite?: (id: string) => void
  /**
   * On business share
   */
  onBusinessShare?: (id: string) => void
  /**
   * Custom className
   */
  className?: string
}

const BusinessGrid = React.forwardRef<HTMLDivElement, BusinessGridProps>(
  (
    {
      businesses,
      loading = false,
      error = null,
      emptyMessage = 'לא נמצאו עסקים',
      onBusinessClick,
      onBusinessFavorite,
      onBusinessShare,
      className = '',
      ...props
    },
    ref
  ) => {
    if (loading) {
      return (
        <Container ref={ref} className={className} {...props}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {Array.from({ length: 8 }).map((_, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden animate-pulse"
              >
                <div className="h-48 sm:h-56 lg:h-64 bg-gray-200" />
                <div className="p-4 sm:p-6">
                  <div className="h-4 bg-gray-200 rounded mb-2" />
                  <div className="h-3 bg-gray-200 rounded mb-4 w-3/4" />
                  <div className="h-3 bg-gray-200 rounded mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </Container>
      )
    }

    if (error) {
      return (
        <Container ref={ref} className={className} {...props}>
          <div className="text-center py-12">
            <div className="text-red-500 text-lg font-medium mb-2">
              שגיאה בטעינת העסקים
            </div>
            <p className="text-gray-600">{error}</p>
          </div>
        </Container>
      )
    }

    if (businesses.length === 0) {
      return (
        <Container ref={ref} className={className} {...props}>
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg font-medium mb-2">
              {emptyMessage}
            </div>
            <p className="text-gray-400">
              נסו לשנות את החיפוש או הסינון
            </p>
          </div>
        </Container>
      )
    }

    return (
      <Container ref={ref} className={className} {...props}>
        <Grid
          cols={1}
          responsive={{
            sm: 2,
            lg: 3,
            xl: 4
          }}
          gap="md"
        >
          {businesses.map((business) => (
            <BusinessCard
              key={business.id}
              {...business}
              onClick={onBusinessClick}
              onFavorite={onBusinessFavorite}
              onShare={onBusinessShare}
            />
          ))}
        </Grid>
      </Container>
    )
  }
)

BusinessGrid.displayName = 'BusinessGrid'

export { BusinessGrid }
